import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * FlyToCart custom element for animating product images to cart.
 * Creates the signature BubbleME flying bubble/ball animation into the cart icon.
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  #hasAnimated = false;

  connectedCallback() {
    super.connectedCallback();

    // Ensure we have a valid destination (cart icon in header)
    if (!this.destination) {
      this.destination =
        document.querySelector('.header-actions__cart-icon') ||
        document.querySelector('[data-testid="cart-drawer-trigger"]') ||
        document.querySelector('cart-icon');
    }

    // Attempt to trigger animation immediately via requestAnimationFrame
    requestAnimationFrame(() => {
      this.#triggerAnimation();
    });
  }

  #triggerAnimation() {
    if (this.#hasAnimated) return;

    // Resolve source element
    const sourceEl = this.source || document.activeElement;
    const sRect = sourceEl?.getBoundingClientRect();

    // Resolve destination element
    const destEl =
      this.destination ||
      document.querySelector('.header-actions__cart-icon') ||
      document.querySelector('[data-testid="cart-drawer-trigger"]') ||
      document.querySelector('cart-icon');

    let dRect = destEl?.getBoundingClientRect();

    // If destination rect is missing or zero-sized (e.g. hidden header), fallback to top-right corner of viewport
    if (!dRect || (dRect.width === 0 && dRect.height === 0)) {
      dRect = {
        left: Math.max(window.innerWidth - 56, 20),
        top: 20,
        width: 36,
        height: 36,
      };
    }

    // If source rect is missing or zero-sized, fallback to center of viewport
    let validSRect = sRect;
    if (!validSRect || (validSRect.width === 0 && validSRect.height === 0)) {
      validSRect = {
        left: window.innerWidth / 2 - 26,
        top: window.innerHeight / 2 - 26,
        width: 52,
        height: 52,
      };
    }

    this.#hasAnimated = true;
    this.#animate(validSRect, dRect);
  }

  /**
   * Animates the flying ball along a parabolic Bezier arc into the cart icon.
   * @param {DOMRect | {left: number, top: number, width: number, height: number}} sourceRect
   * @param {DOMRect | {left: number, top: number, width: number, height: number}} destinationRect
   */
  #animate = async (sourceRect, destinationRect) => {
    const startX = sourceRect.left + sourceRect.width / 2;
    const startY = sourceRect.top + sourceRect.height / 2;

    const endX = destinationRect.left + destinationRect.width / 2;
    const endY = destinationRect.top + destinationRect.height / 2;

    const dx = endX - startX;
    const dy = endY - startY;

    // Calculate control point for graceful upward parabolic curve.
    // The ball arches upward and across towards the cart icon.
    const midX = startX + dx * 0.45;
    const topY = Math.min(startY, endY);
    const archLift = Math.max(70, Math.min(220, Math.abs(dx) * 0.28 + Math.abs(dy) * 0.15));
    let midY = topY - archLift;
    // Ensure the arc stays comfortably on-screen
    if (midY < 18) {
      midY = Math.max(12, topY - 25);
    }

    // Build keyframes along the parabolic Bezier curve
    const keyframes = [];
    const steps = 14;
    for (let step = 0; step <= steps; step++) {
      const t = step / steps;
      const inv = 1 - t;

      // Quadratic Bezier: B(t) = (1-t)^2 * P0 + 2*(1-t)*t * P1 + t^2 * P2
      const x = inv * inv * startX + 2 * inv * t * midX + t * t * endX;
      const y = inv * inv * startY + 2 * inv * t * midY + t * t * endY;

      // Scale: Starts at 0.92, gentle pop to 1.08 in early flight, then tapers down into 0.26
      let scale;
      if (t < 0.18) {
        scale = 0.92 + (t / 0.18) * 0.16;
      } else if (t < 0.55) {
        scale = 1.08 - ((t - 0.18) / 0.37) * 0.18;
      } else {
        scale = 0.90 - ((t - 0.55) / 0.45) * 0.64;
      }

      // Opacity: Solid throughout flight, quick fade in the last 6% inside the cart icon
      let opacity = 1;
      if (t < 0.08) {
        opacity = 0.5 + (t / 0.08) * 0.5;
      } else if (t > 0.94) {
        opacity = 1 - ((t - 0.94) / 0.06) * 0.85;
      }

      keyframes.push({
        transform: `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%) scale(${scale.toFixed(3)})`,
        opacity: Number(opacity.toFixed(2)),
      });
    }

    // Initial positioning before animation plays
    this.style.transform = `translate3d(${startX}px, ${startY}px, 0) translate(-50%, -50%) scale(0.92)`;
    this.style.opacity = '1';

    await yieldToMainThread();

    // Use Web Animations API
    if (typeof this.animate === 'function') {
      try {
        const anim = this.animate(keyframes, {
          duration: 620,
          easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
          fill: 'forwards',
        });
        await anim.finished;
      } catch (err) {
        await new Promise((r) => setTimeout(r, 620));
      }
    } else {
      await new Promise((r) => setTimeout(r, 620));
    }

    // Pulse the destination cart icon
    const bounceTarget =
      this.destination ||
      document.querySelector('.header-actions__cart-icon') ||
      document.querySelector('[data-testid="cart-drawer-trigger"]') ||
      document.querySelector('cart-icon');

    if (bounceTarget) {
      bounceTarget.classList.add('cart-icon--bounce');
      setTimeout(() => {
        bounceTarget.classList.remove('cart-icon--bounce');
      }, 500);
    }

    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
